// Polygon triangulation + mesh-building helpers used by water and irregular mirror planes.
// Ear-clipping for simple (possibly non-convex) polygons. No hole support.

import * as pc from 'playcanvas';

export type Polygon2D = Array<{ u: number; v: number }>;

const EPS = 1e-9;

function area2d(p: Polygon2D): number {
    let s = 0;
    for (let i = 0, n = p.length; i < n; i++) {
        const a = p[i];
        const b = p[(i + 1) % n];
        s += a.u * b.v - b.u * a.v;
    }
    return s * 0.5;
}

function samePoint(a: { u: number; v: number }, b: { u: number; v: number }): boolean {
    return Math.abs(a.u - b.u) <= EPS && Math.abs(a.v - b.v) <= EPS;
}

function orient(a: { u: number; v: number }, b: { u: number; v: number }, c: { u: number; v: number }): number {
    return (b.u - a.u) * (c.v - a.v) - (b.v - a.v) * (c.u - a.u);
}

function onSegment(a: { u: number; v: number }, b: { u: number; v: number }, p: { u: number; v: number }): boolean {
    return (
        Math.min(a.u, b.u) - EPS <= p.u &&
        p.u <= Math.max(a.u, b.u) + EPS &&
        Math.min(a.v, b.v) - EPS <= p.v &&
        p.v <= Math.max(a.v, b.v) + EPS &&
        Math.abs(orient(a, b, p)) <= EPS
    );
}

function segmentsIntersect(
    a: { u: number; v: number },
    b: { u: number; v: number },
    c: { u: number; v: number },
    d: { u: number; v: number }
): boolean {
    const o1 = orient(a, b, c);
    const o2 = orient(a, b, d);
    const o3 = orient(c, d, a);
    const o4 = orient(c, d, b);

    if (Math.abs(o1) <= EPS && onSegment(a, b, c)) return true;
    if (Math.abs(o2) <= EPS && onSegment(a, b, d)) return true;
    if (Math.abs(o3) <= EPS && onSegment(c, d, a)) return true;
    if (Math.abs(o4) <= EPS && onSegment(c, d, b)) return true;

    return (
        ((o1 > EPS && o2 < -EPS) || (o1 < -EPS && o2 > EPS)) &&
        ((o3 > EPS && o4 < -EPS) || (o3 < -EPS && o4 > EPS))
    );
}

function hasSelfIntersections(poly: Polygon2D): boolean {
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        for (let j = i + 1; j < poly.length; j++) {
            const adjacent = Math.abs(i - j) === 1 || (i === 0 && j === poly.length - 1);
            if (adjacent) continue;
            const c = poly[j];
            const d = poly[(j + 1) % poly.length];
            if (segmentsIntersect(a, b, c, d)) return true;
        }
    }
    return false;
}

/** True when two non-adjacent edges of the closed polygon cross or touch. */
export function polygonSelfIntersects(poly: Polygon2D): boolean {
    return poly.length >= 4 && hasSelfIntersections(poly);
}

function pointInTriangle(
    px: number, py: number,
    ax: number, ay: number,
    bx: number, by: number,
    cx: number, cy: number
): boolean {
    const d1 = (px - bx) * (ay - by) - (ax - bx) * (py - by);
    const d2 = (px - cx) * (by - cy) - (bx - cx) * (py - cy);
    const d3 = (px - ax) * (cy - ay) - (cx - ax) * (py - ay);
    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
    return !(hasNeg && hasPos);
}

/**
 * Ear-clipping triangulation. Returns flat triangle index list into the input polygon.
 * Handles concave simple polygons. Input must not self-intersect.
 */
export function triangulatePolygon(poly: Polygon2D): number[] {
    const n = poly.length;
    if (n < 3) return [];
    if (Math.abs(area2d(poly)) <= EPS || hasSelfIntersections(poly)) return [];
    if (n === 3) return [0, 1, 2];

    // Normalize winding to CCW so cross > 0 is convex.
    const ccw = area2d(poly) > 0;
    const idx: number[] = ccw
        ? poly.map((_, i) => i)
        : poly.map((_, i) => n - 1 - i);

    const out: number[] = [];
    let guard = idx.length * idx.length;

    while (idx.length > 3 && guard-- > 0) {
        let earFound = false;
        for (let i = 0; i < idx.length; i++) {
            const i0 = idx[(i + idx.length - 1) % idx.length];
            const i1 = idx[i];
            const i2 = idx[(i + 1) % idx.length];
            const a = poly[i0];
            const b = poly[i1];
            const c = poly[i2];
            const cross = (b.u - a.u) * (c.v - a.v) - (b.v - a.v) * (c.u - a.u);
            if (cross <= EPS) continue;
            let containsOther = false;
            for (let j = 0; j < idx.length; j++) {
                const ij = idx[j];
                if (ij === i0 || ij === i1 || ij === i2) continue;
                const p = poly[ij];
                if (pointInTriangle(p.u, p.v, a.u, a.v, b.u, b.v, c.u, c.v)) {
                    containsOther = true;
                    break;
                }
            }
            if (containsOther) continue;
            out.push(i0, i1, i2);
            idx.splice(i, 1);
            earFound = true;
            break;
        }
        if (!earFound) {
            return [];
        }
    }
    if (idx.length === 3) {
        out.push(idx[0], idx[1], idx[2]);
    }
    return out;
}

export const DEFAULT_RECT_FOOTPRINT: Polygon2D = [
    { u: -1, v: -1 },
    { u: 1, v: -1 },
    { u: 1, v: 1 },
    { u: -1, v: 1 },
];

export interface PolygonBounds {
    minU: number;
    maxU: number;
    minV: number;
    maxV: number;
}

export function polygonBounds(poly: Polygon2D): PolygonBounds {
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of poly) {
        if (p.u < minU) minU = p.u;
        if (p.u > maxU) maxU = p.u;
        if (p.v < minV) minV = p.v;
        if (p.v > maxV) maxV = p.v;
    }
    if (!isFinite(minU)) { minU = -1; maxU = 1; minV = -1; maxV = 1; }
    return { minU, maxU, minV, maxV };
}

export interface PolygonMeshLayout {
    /** Maps a 2D footprint vertex (u, v) into local 3D space (x, y, z). */
    layout: (u: number, v: number) => [number, number, number];
    /** Plane normal in local space. */
    normal: [number, number, number];
}

export const HORIZONTAL_LAYOUT: PolygonMeshLayout = {
    layout: (u, v) => [u, 0, v],
    normal: [0, 1, 0],
};

export const VERTICAL_LAYOUT: PolygonMeshLayout = {
    layout: (u, v) => [u, v, 0],
    normal: [0, 0, 1],
};

/**
 * Build a `pc.Mesh` from a polygon footprint. UVs are normalized to the polygon's
 * 2D bounding box so wave textures look uniform regardless of shape.
 */
export function buildPolygonMesh(
    device: pc.GraphicsDevice,
    poly: Polygon2D,
    layoutMode: PolygonMeshLayout = HORIZONTAL_LAYOUT
): pc.Mesh | null {
    if (poly.length < 3) return null;
    const tris = triangulatePolygon(poly);
    if (tris.length === 0) return null;

    const bounds = polygonBounds(poly);
    const uSpan = Math.max(bounds.maxU - bounds.minU, 1e-6);
    const vSpan = Math.max(bounds.maxV - bounds.minV, 1e-6);

    const positions = new Array<number>(poly.length * 3);
    const normals = new Array<number>(poly.length * 3);
    const uvs = new Array<number>(poly.length * 2);
    for (let i = 0; i < poly.length; i++) {
        const p = poly[i];
        const [x, y, z] = layoutMode.layout(p.u, p.v);
        positions[i * 3 + 0] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;
        normals[i * 3 + 0] = layoutMode.normal[0];
        normals[i * 3 + 1] = layoutMode.normal[1];
        normals[i * 3 + 2] = layoutMode.normal[2];
        uvs[i * 2 + 0] = (p.u - bounds.minU) / uSpan;
        uvs[i * 2 + 1] = (p.v - bounds.minV) / vSpan;
    }

    const mesh = new pc.Mesh(device);
    mesh.setPositions(positions);
    mesh.setNormals(normals);
    mesh.setUvs(0, uvs);
    mesh.setIndices(tris);
    mesh.update();
    return mesh;
}

type FootprintInputVertex = {
    u?: number | string;
    v?: number | string;
    x?: number | string;
    y?: number | string;
    z?: number | string;
};

type FootprintInputValue = FootprintInputVertex | null | undefined | number | string | boolean;

export function sanitizeFootprint(poly: ReadonlyArray<FootprintInputValue> | null | undefined): Polygon2D | null {
    if (!Array.isArray(poly) || poly.length < 3) return null;
    const out: Polygon2D = [];
    for (const raw of poly) {
        if (!raw || typeof raw !== 'object') return null;
        const u = Number(raw.u ?? raw.x);
        const v = Number(raw.v ?? raw.z ?? raw.y);
        if (!isFinite(u) || !isFinite(v)) return null;
        const next = { u, v };
        if (out.length === 0 || !samePoint(out[out.length - 1], next)) {
            out.push(next);
        }
    }
    if (out.length > 1 && samePoint(out[0], out[out.length - 1])) {
        out.pop();
    }
    if (out.length < 3) return null;
    if (Math.abs(area2d(out)) <= EPS) return null;
    if (hasSelfIntersections(out)) return null;
    return out;
}
